import _ from "lodash";
import { useMemo } from "react";
import { useMeasure, useWindowSize } from "react-use";

import { SubscriberController } from "../../../../../services/controllers/subscription";

import { useSubscribers } from "../../../../../store/subscription/useSubscribers";

import { Alert } from "../../../../../components/ui/Alert";
import { Table } from "../../../../../components/ui/Table";
import { ScrollArea } from "../../../../../components/ui/ScrollArea";

import { TransactionBadgeStatus } from "./transaction-badge/transaction-badge-status";
import { TransactionBadgeMethod } from "./transaction-badge/transaction-badge-method";

import { TransactionBadgeType } from "./transaction-badge/transaction-badge-type";
import { Action } from "../../../../../components/ui/Action";
import { CreditCard, PixLogo } from "@phosphor-icons/react";
import { useState } from "react";
import { SwitchCamera } from "lucide-react";

export function AlertCycleTransactions() {
  const CSubscriber = SubscriberController();

  const window = useWindowSize();
  const [ref, list] = useMeasure();
  const [action, setAction] = useState();

  const details = useSubscribers(st => st.data.details)
  const isProcessing = useSubscribers(st => st.data.isProcessing)
  const cycleTransactions = useSubscribers(st => st.data.cycleTransactions)


  const listHeight = useMemo(() => {
    const transactions = Math.min(cycleTransactions.length, 8);
    const maxHeight = transactions * 52

    return (list.height.toFixed(0) >= maxHeight) ? `${maxHeight}px` : 'fit-content'

  }, [window.height, list.height, cycleTransactions])

  if (!details) return null

  const rechargeProps = { text: 'Recobrar', color: '#f97b18' }
  const changeMethodProps = { text: 'Trocar metodo', color: '#f97b18' }

  const showRetry = CSubscriber.selectedCycle?.status.code === 'failed' && details.status === 'Em atraso';

  const isLast = _.last(details.cycles).id === CSubscriber.selectedCycle?.id

  if (cycleTransactions[0]?.payment.method.code === 'pix') {
    rechargeProps.text = 'Gerar novo Pix'
    rechargeProps.icon = PixLogo
    rechargeProps.color = 'oklch(70.4% 0.14 182.503)'

    changeMethodProps.code = 'credit_card'
    changeMethodProps.text = 'Pagar com cartão de crédito'
    changeMethodProps.icon = CreditCard
    changeMethodProps.color = 'oklch(0.746 0.16 232.661)'
  }

  if (cycleTransactions[0]?.payment.method.code === 'credit_card') {
    rechargeProps.text = 'Recobrar no cartão'
    rechargeProps.icon = CreditCard
    rechargeProps.color = 'oklch(0.746 0.16 232.661)'

    changeMethodProps.code = 'pix'
    changeMethodProps.text = 'Pagar com Pix'
    changeMethodProps.icon = PixLogo
    changeMethodProps.color = 'oklch(70.4% 0.14 182.503)'
  }

  return (
    <Alert.Root name={CSubscriber.cycleTransactionsAlertName}>
      <Alert.Content noScroll style={{ maxWidth: '1200px' }}>
        <Alert.Title>Histórico de cobranças</Alert.Title>
        <div className='flex w-full justify-between mb-6'>
          <Action.Root
            type="button"
            custom={rechargeProps.color}
            disabled={!showRetry || isProcessing}
            onClick={() => {
              setAction('recharge')
              CSubscriber.tryRecharge(details, CSubscriber.selectedCycle)
            }}
          >
            <Action.Icon icon={rechargeProps?.icon} isLoading={isProcessing && action === 'recharge'} className="font-semibold" weight="regular" size={24} />
            {rechargeProps.text}
          </Action.Root>

          <Action.Root
            outline
            type="button"
            custom={changeMethodProps.color}
            isLoading={!showRetry || isProcessing}
            onClick={() => {
              setAction('change-method')
              CSubscriber.changeMethodSubscribers(details, CSubscriber.selectedCycle, { code: changeMethodProps.code })
            }}
          >
            <Action.Icon icon={changeMethodProps.icon} isLoading={isProcessing && action === 'change-method'} className="font-semibold" weight="regular" size={24} />  {changeMethodProps.text}
          </Action.Root>
        </div>
        <Table.Root name={'subscriptions-cycle-transactions-table'} config={111} mergeLast>
          <Table.Header
            columns={[
              "Data:70px",
              "Hora:65px:true",
              "Tipo|pl-4.5:155px",
              "Método:80px",
              "Valor:100px",
              "Mensagem:1fr",
              ":100px",
              "Status:215px",
              "_:50px",
            ]}
          />
          <div ref={ref} className>
            <Table.Content>
              <ScrollArea.Root height={listHeight} className="!p-0">
                {cycleTransactions?.map((transaction, i) => (
                  <Table.Line key={transaction.id}>
                    <Table.Item className="[&>span]:!px-2">{transaction.transactionDate}</Table.Item>
                    <Table.Item className="[&>span]:!px-1.5">{transaction.transactionHour}</Table.Item>
                    <Table.Item className="[&>span]:!pl-4.5">
                      <TransactionBadgeType type={transaction.type} />
                    </Table.Item>
                    <Table.Item className="[&_div]:mx-auto">
                      <TransactionBadgeMethod method={transaction.payment.method} transactionId={transaction.id} />
                    </Table.Item>
                    <Table.Item>R$ {transaction.amount}</Table.Item>
                    <Table.Item>{transaction.gateway.message}</Table.Item>
                    <Table.Item>
                      {(transaction.payment.method.code === 'pix' && transaction.status.code === 'waiting') && (
                        <button
                          type="button"
                          className="underline cursor-pointer text-orange-500 font-semibold"
                          onClick={() => { CSubscriber.openPixCode(transaction) }}
                        >
                          Ver código
                        </button>
                      )}
                    </Table.Item>
                    <Table.Item className="[&>span]:!pl-0 !pr-px">
                      <TransactionBadgeStatus status={transaction.status} />
                    </Table.Item>
                    <Table.Item className="">
                      {!i && isLast && transaction.status.code === 'success' && transaction.payment.method.code === 'credit_card' && (
                        <Action.Root
                          // outline
                          type="button"
                          status="warning"
                          title="Estronar cobrança"
                          name={`refund-payment-${transaction.id}`}
                          className="!py-1"
                          onClick={() => CSubscriber.refundTransactions(details)}
                        >
                          <Action.Icon icon={SwitchCamera} size={26} className="-scale-x-100" />
                        </Action.Root>
                      )}
                    </Table.Item>
                  </Table.Line>
                ))}
              </ScrollArea.Root>
            </Table.Content>
          </div>
        </Table.Root>
        <Alert.Actions>
          <Alert.Confirm onClick={CSubscriber.closeCycleTransactions}>
            Fechar
          </Alert.Confirm>
        </Alert.Actions>
      </Alert.Content>
    </Alert.Root >
  )
}
